/**
 * Shop — Product catalogue with search, filters, and sorting.
 * Uses new shared components: SearchInput, ProductGrid, Reveal.
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { SlidersHorizontal } from "lucide-react";
import { ProductGrid } from "@/features/products/components/product-grid";
import { Reveal } from "@/components/common/reveal";
import { SearchInput } from "@/components/forms/search-input";
import { EmptyState } from "@/components/common/empty-state";
import { LuxuryCategorySlider } from "@/components/common/LuxuryCategorySlider";
import { useLiveProducts } from "@/features/products/hooks/use-live-products";
import { siteConfig } from "@/config/site";
import { products } from "@/lib/products";

export const Route = createFileRoute("/shop")({
  // The return type is annotated with OPTIONAL keys on purpose. Without it TypeScript
  // infers `{ category: string | undefined; search: string | undefined }` -- properties
  // that must be *present* even when undefined -- which makes every `<Link to="/shop">`
  // in the app a type error unless it passes a search object. Same convention as
  // account/login.tsx and account/register.tsx.
  validateSearch: (
    search: Record<string, unknown>,
  ): { category?: string; search?: string; sort?: string } => ({
    category: (search.category as string) || (search.cat as string) || undefined,
    search: (search.search as string) || (search.q as string) || undefined,
    sort: (search.sort as string) || undefined,
  }),
  head: () => ({
    meta: [
      // "Shop All Botanicals" was the previous title. Nobody searches "botanicals"; this is
      // the catalogue page, so it leads with the terms that describe what is in it.
      { title: `Shop Artificial Flowers & Plants Online — ${siteConfig.name}` },
      {
        name: "description",
        content: `Shop all ${products.length} artificial flowers and fake plants — faux bonsai trees, 5 FT statement trees, silk flower bushes, hanging vines, and potted botanicals with pots included.`,
      },
      {
        property: "og:title",
        content: `Shop Artificial Flowers & Plants Online — ${siteConfig.shortName}`,
      },
      {
        property: "og:description",
        content:
          "Artificial flowers, faux bonsai, 5 FT statement trees, hanging vines, and potted indoor plants.",
      },
    ],
    links: [{ rel: "canonical", href: `${siteConfig.url}/shop` }],
  }),
  component: Shop,
});

type SortOption = "featured" | "price-low" | "price-high" | "rating";
const SORT_OPTIONS: SortOption[] = ["featured", "price-low", "price-high", "rating"];

function Shop() {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  const { products: productList, loading } = useLiveProducts();
  const [activeCategory, setActiveCategory] = useState<string>(
    () => searchParams.category || "All",
  );
  const [searchQuery, setSearchQuery] = useState(() => searchParams.search || "");
  const [sortBy, setSortBy] = useState<SortOption>(() =>
    (SORT_OPTIONS as string[]).includes(searchParams.sort || "")
      ? (searchParams.sort as SortOption)
      : "featured",
  );

  // URL -> state (deep links, back-button)
  useEffect(() => {
    if (searchParams.category) {
      setActiveCategory(searchParams.category);
    }
    if (searchParams.search !== undefined) {
      setSearchQuery(searchParams.search);
    }
    if (searchParams.sort && (SORT_OPTIONS as string[]).includes(searchParams.sort)) {
      setSortBy(searchParams.sort as SortOption);
    }
  }, [searchParams.category, searchParams.search, searchParams.sort]);

  // State -> URL: keep filters deep-linkable so refresh/back/share preserve them.
  const syncSearch = (patch: { category?: string; search?: string }) => {
    navigate({
      to: "/shop",
      search: (prev) => ({ ...prev, ...patch }),
      replace: true,
    });
  };
  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    syncSearch({ category: category === "All" ? "" : category });
  };
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    syncSearch({ search: query });
  };

  const categories = useMemo(() => {
    const valid = productList.map((p) => p.category).filter(Boolean);
    return ["All", ...Array.from(new Set(valid))];
  }, [productList]);

  const filteredProducts = useMemo(() => {
    let list =
      activeCategory === "All"
        ? productList
        : productList.filter(
            (p) => (p.category || "").toLowerCase() === activeCategory.toLowerCase(),
          );

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.category || "").toLowerCase().includes(q) ||
          (p.note || "").toLowerCase().includes(q) ||
          (p.description || "").toLowerCase().includes(q),
      );
    }

    switch (sortBy) {
      case "price-low":
        return [...list].sort((a, b) => a.price - b.price);
      case "price-high":
        return [...list].sort((a, b) => b.price - a.price);
      case "rating":
        return [...list].sort((a, b) => (b.rating || 0) - (a.rating || 0));
      default:
        return list;
    }
  }, [activeCategory, searchQuery, sortBy, productList]);

  return (
    <div className="section-container pb-28 pt-24 md:pt-28">
      {/* This page had no <h1> at all -- it opened directly onto the controls bar, so the
          catalogue page competing for the site's main commercial query had no heading for a
          crawler to weigh. The subheading below carries the brand voice. */}
      <header className="mb-10 max-w-3xl">
        <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground">
          Artificial Flowers &amp; Plants
        </h1>
        <p className="mt-4 text-base md:text-lg leading-relaxed text-muted-foreground">
          The full range — silk flower bushes, sculpted faux bonsai, clipped topiary and indoor
          foliage. Every piece stands between 18 and 29 cm and arrives already potted.
        </p>
      </header>

      {/* Controls bar */}
      <div className="flex flex-row items-center gap-3 md:justify-between">
        <div className="flex-1 max-w-md">
          <SearchInput
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search botanicals, styles or pots…"
          />
        </div>

        <div className="flex items-center shrink-0">
          <label htmlFor="sort-select" className="sr-only">
            Sort products
          </label>
          <select
            id="sort-select"
            aria-label="Sort products"
            value={sortBy}
            onChange={(e) => {
              const next = e.target.value as SortOption;
              setSortBy(next);
              navigate({
                to: "/shop",
                search: (prev) => ({ ...prev, sort: next }),
                replace: true,
              });
            }}
            className="h-11 sm:h-12 rounded-full border border-stone-300/80 bg-white text-stone-900 px-3 sm:px-5 text-xs sm:text-sm font-semibold shadow-xs outline-none transition-[border-color,box-shadow] duration-200 hover:border-stone-400 focus-visible:border-emerald-800 focus-visible:ring-2 focus-visible:ring-emerald-800/15 cursor-pointer dark:bg-stone-900 dark:text-stone-100"
          >
            <option value="featured">Sort by: Featured</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
          </select>
        </div>
      </div>

      {/* Luxury 3D Cutout Category Slider Bar (Image 1 style) */}
      <LuxuryCategorySlider
        activeCategory={activeCategory}
        onSelectCategory={handleCategoryChange}
        className="mt-8 mb-4"
      />

      {/* Results */}
      {filteredProducts.length === 0 ? (
        <EmptyState
          title="No botanicals found"
          description="Try resetting your search query or choosing another category."
          action={{ label: "Reset Filters", to: "/shop" }}
        >
          <button
            onClick={() => {
              setSearchQuery("");
              setActiveCategory("All");
              syncSearch({ category: "", search: "" });
            }}
            className="mt-4 text-xs text-muted-foreground underline transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-800"
          >
            Clear all filters
          </button>
        </EmptyState>
      ) : (
        <div className="mt-14">
          <Reveal>
            <ProductGrid products={filteredProducts} />
          </Reveal>
        </div>
      )}
    </div>
  );
}
